import type { Props } from 'davaux/jsx-runtime'
import type { StyleObject, StyleProps, UiColor, UiGradient, UiRadius, UiSize } from './types.js'
import { cx, fontSize, mergeStyle, pickStyleProps, radius, resolveVariantColors } from './types.js'

const ROOT = 'dv-button'
const INNER = 'dv-button-inner'
const LABEL = 'dv-button-label'
const SECTION = 'dv-button-section'
const GROUP = 'dv-button-group'
const GROUP_SECTION = 'dv-button-group-section'

/** Visual style variant controlling background, border, and color treatment. */
export type ButtonVariant =
  | 'filled'
  | 'light'
  | 'outline'
  | 'subtle'
  | 'transparent'
  | 'white'
  | 'default'
  | 'gradient'

/** Props for the Button component. */
export interface ButtonProps extends StyleProps {
  /** Visual style variant applied to the button. */
  variant?: ButtonVariant
  /** Base color used to derive background, hover, and border from the theme palette. */
  color?: UiColor
  /** Button size; prefix with `compact-` for reduced padding variants. */
  size?: UiSize | `compact-${UiSize}`
  /** Border-radius of the button corners. */
  radius?: UiRadius
  /** Stretches the button to fill its container width. */
  fullWidth?: boolean
  /** Disables interaction and applies disabled styling. */
  disabled?: boolean
  /** Shows a loading state and prevents interaction. */
  loading?: boolean
  /** CSS `justify-content` value for the inner content alignment. */
  justify?: string
  /** Gradient definition used when `variant` is `'gradient'`. */
  gradient?: UiGradient
  /** Icon or content placed before the label. */
  leftSection?: Props['children']
  /** Icon or content placed after the label. */
  rightSection?: Props['children']
  /** HTML tag or component to render as the root element (default `'button'`). */
  component?: string
  /** Native button type attribute. */
  type?: 'button' | 'submit' | 'reset'
  /** Label text or content rendered inside the button. */
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a styled button element with support for variants, icons, loading state, and gradient fills. */
export function Button({
  variant = 'filled',
  color,
  size,
  radius: r,
  fullWidth,
  disabled,
  loading,
  justify,
  gradient,
  leftSection,
  rightSection,
  component = 'button',
  type = 'button',
  class: className,
  style,
  children,
  ...rest
}: ButtonProps): Promise<string> {
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const cssVars: StyleObject = {}

  if (justify !== undefined) cssVars['--dv-button-justify'] = justify

  const baseSize = size?.startsWith('compact-') ? size.replace('compact-', '') : size
  if (baseSize !== undefined) {
    cssVars['--dv-button-height'] = `var(--dv-button-height-${size})`
    cssVars['--dv-button-padding-x'] = `var(--dv-button-padding-x-${size})`
    cssVars['--dv-button-fz'] = fontSize(baseSize as UiSize) ?? ''
  }

  if (r !== undefined) cssVars['--dv-button-radius'] = radius(r) ?? ''

  if (variant === 'gradient' && gradient) {
    const deg = gradient.deg ?? 45
    cssVars['--dv-button-bg'] = `linear-gradient(${deg}deg, ${gradient.from}, ${gradient.to})`
    cssVars['--dv-button-hover'] = `linear-gradient(${deg}deg, ${gradient.from}, ${gradient.to})`
    cssVars['--dv-button-color'] = 'var(--dv-color-white)'
    cssVars['--dv-button-bd'] = 'none'
  } else if (color !== undefined || variant !== 'filled') {
    const colors = resolveVariantColors(color, variant)
    cssVars['--dv-button-bg'] = colors.background
    cssVars['--dv-button-hover'] = colors.hover
    cssVars['--dv-button-color'] = colors.color
    cssVars['--dv-button-bd'] = colors.border
  }

  const Tag = component as unknown as (p: Props) => Promise<string>
  return (
    <Tag
      class={cx(ROOT, className)}
      style={mergeStyle({ ...cssVars, ...styleObj }, style)}
      data-variant={variant}
      data-disabled={disabled || undefined}
      data-loading={loading || undefined}
      data-full-width={fullWidth || undefined}
      data-size={size}
      data-with-left-section={leftSection !== undefined || undefined}
      data-with-right-section={rightSection !== undefined || undefined}
      type={type}
      disabled={disabled || loading}
      {...ownRest}
    >
      <span class={INNER}>
        {leftSection && (
          <span class={SECTION} data-position='left'>
            {leftSection}
          </span>
        )}
        <span class={LABEL}>{children}</span>
        {rightSection && (
          <span class={SECTION} data-position='right'>
            {rightSection}
          </span>
        )}
      </span>
    </Tag>
  )
}

/** Props for Button.Group — arranges buttons into a connected row or column. */
export interface ButtonGroupProps extends StyleProps {
  /** Layout direction of the grouped buttons. */
  orientation?: 'horizontal' | 'vertical'
  /** Button elements or ButtonGroupSection elements to render inside the group. */
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a connected group of buttons sharing borders and radius treatment. */
export function ButtonGroup({
  orientation = 'horizontal',
  class: className,
  style,
  children,
  ...rest
}: ButtonGroupProps): Promise<string> {
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  return (
    <div
      class={cx(GROUP, className)}
      style={mergeStyle(styleObj, style)}
      data-orientation={orientation}
      {...ownRest}
    >
      {children}
    </div>
  )
}

/** Props for Button.GroupSection — a non-button element rendered inside a Button.Group. */
export interface ButtonGroupSectionProps extends StyleProps {
  /** HTML tag or component to render as the root element (default `'div'`). */
  component?: string
  /** Content rendered inside the section. */
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a non-button section inside a Button.Group, sharing the group's border treatment. */
export function ButtonGroupSection({
  component = 'div',
  class: className,
  style,
  children,
  ...rest
}: ButtonGroupSectionProps): Promise<string> {
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const Tag = component as unknown as (p: Props) => Promise<string>
  return (
    <Tag class={cx(GROUP_SECTION, className)} style={mergeStyle(styleObj, style)} {...ownRest}>
      {children}
    </Tag>
  )
}

Button.Group = ButtonGroup
Button.GroupSection = ButtonGroupSection
